import React, { useEffect, useState } from "react";
import { getCurrencySymbol } from "../../../../../tenant/currency";
import { NumericFormat } from "react-number-format";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import styles from "./borrow-token-form.module.scss";
import { BorrowEligibilityData } from "../../buy-electricity.types";
import { useLazyVerifyPrepaidMeterQuery } from "../../../../../service/vending-service";
import { VerifyMeterNumberPayload, VerifyPrepaidMeterResponse } from "../../../../../model/charge";
import { getErrorMessage } from "../../../../../utils/error-handler";
import Button from "../../../../../common/ekedc-components/button/Button";
import { SwitchButton } from "../../../../../common/components/switch-button/SwitchButton";

interface BorrowTokenFormProps {
	/** When provided (e.g. coming from Buy Electricity → confirmation), the meter is
	 * already verified and the meter card is skipped. */
	eligibilityData?: BorrowEligibilityData;
	currency: string;
	utilityId?: string;
	initialMeterNumber?: string;
	initialAmount?: number;
	onCancel: () => void;
	onBorrow: (amount: number) => void;
	onMeterVerified?: (data: {
		meterNumber: string;
		eligibility: BorrowEligibilityData;
		response: VerifyPrepaidMeterResponse;
	}) => void;
}

const sym = (currency: string) => getCurrencySymbol(currency);

export const BorrowTokenForm: React.FC<BorrowTokenFormProps> = ({
	eligibilityData,
	currency,
	utilityId,
	initialMeterNumber,
	initialAmount,
	onBorrow,
	onMeterVerified,
}) => {
	const { t } = useTranslation();
	const [meterNumber, setMeterNumber] = useState(initialMeterNumber ?? "");
	const [isValidatingMeter, setIsValidatingMeter] = useState(false);
	const [meterError, setMeterError] = useState<string>("");
	const [verifiedAccountName, setVerifiedAccountName] = useState<string>("");
	const [internalEligibility, setInternalEligibility] = useState<BorrowEligibilityData | null>(null);
	const [lastValidatedMeter, setLastValidatedMeter] = useState("");

	const [verifyPrepaidMeter] = useLazyVerifyPrepaidMeterQuery();

	// Effective eligibility = prop (from buy-electricity flow) OR locally-verified
	const effectiveEligibility = eligibilityData ?? internalEligibility;
	// Lock the meter-card visibility to the FIRST render so it doesn't disappear
	// after onMeterVerified lifts eligibility into the orchestrator and flows it back.
	const [needsMeter] = useState(() => !eligibilityData);
	const meterReady = !!effectiveEligibility;

	const [amount, setAmount] = useState(initialAmount?.toString() || "");
	const [authorized, setAuthorized] = useState(false);
	const [touched, setTouched] = useState(false);

	const s = sym(currency);
	const minimumBorrowAmount = effectiveEligibility?.minimumBorrowAmount ?? 0;
	const maximumBorrowAmount = effectiveEligibility?.maximumBorrowAmount ?? 0;
	const minimumVendAmount = effectiveEligibility?.minimumVendAmount ?? 0;
	const parsedAmount = parseFloat(amount) || 0;

	// Auto-verify meter once user types ≥ 9 digits, debounced 600ms.
	useEffect(() => {
		if (!needsMeter) return;
		if (!utilityId) return;
		if (meterNumber.length < 9) {
			if (verifiedAccountName || meterError || internalEligibility) {
				setVerifiedAccountName("");
				setMeterError("");
				setInternalEligibility(null);
				setLastValidatedMeter("");
			}
			return;
		}
		if (meterNumber === lastValidatedMeter) return;

		const timer = setTimeout(async () => {
			setLastValidatedMeter(meterNumber);
			setIsValidatingMeter(true);
			setMeterError("");
			try {
				const payload: VerifyMeterNumberPayload = { MeterNumber: meterNumber, UtilityId: utilityId };
				const resp = await verifyPrepaidMeter(payload).unwrap();
				const eligibility: BorrowEligibilityData = {
					minimumBorrowAmount: resp.minimumBorrowAmount ?? 0,
					maximumBorrowAmount: resp.maximumBorrowAmount ?? 0,
					minimumVendAmount: resp.minimumVendAmount,
					outstandingDebt: resp.outstandingDebt ?? 0,
				};
				if (eligibility.maximumBorrowAmount <= 0) {
					setMeterError(t("buyElectricity.web.meterNotEligibleToBorrow"));
					return;
				}
				if (eligibility.outstandingDebt > 0) {
					setMeterError(
						t("buyElectricity.web.outstandingBalanceSettleBeforeBorrow", { amount: `${s}${eligibility.outstandingDebt.toLocaleString()}` })
					);
					return;
				}
				setVerifiedAccountName(resp.accountName ?? "");
				setInternalEligibility(eligibility);
				if (onMeterVerified) {
					onMeterVerified({ meterNumber, eligibility, response: resp });
				}
			} catch (err: any) {
				setMeterError(getErrorMessage(err?.data) ?? t("buyElectricity.web.couldntVerifyMeterAsPrepaid"));
				setInternalEligibility(null);
				setVerifiedAccountName("");
			} finally {
				setIsValidatingMeter(false);
			}
		}, 600);

		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meterNumber, utilityId, needsMeter]);

	const getAmountError = (): string | null => {
		if (!touched || !amount) return null;
		if (parsedAmount < minimumBorrowAmount)
			return t("buyElectricity.web.minimumBorrowAmountIs", { amount: `${s}${minimumBorrowAmount.toLocaleString()}` });
		if (parsedAmount > maximumBorrowAmount)
			return t("buyElectricity.web.maximumBorrowAmountIs", { amount: `${s}${maximumBorrowAmount.toLocaleString()}` });
		if (parsedAmount < minimumVendAmount)
			return t("buyElectricity.web.amountMustBeAtLeastToPurchase", { amount: `${s}${minimumVendAmount.toLocaleString()}` });
		return null;
	};

	const amountError = getAmountError();
	const amountIsValid =
		meterReady &&
		parsedAmount >= minimumBorrowAmount &&
		parsedAmount <= maximumBorrowAmount &&
		parsedAmount >= minimumVendAmount;
	const isValid = authorized && amountIsValid;

	const handleBorrow = () => {
		setTouched(true);
		if (!meterReady) {
			toast.error(t("buyElectricity.web.verifyPrepaidMeterFirst"));
			return;
		}
		if (isValid) {
			onBorrow(parsedAmount);
		}
	};

	const payLabel = parsedAmount > 0 ? t("borrowToken.payAmount", { amount: `${s}${parsedAmount.toLocaleString()}` }) : t("pin.pay");

	return (
		<form
			className={styles.form}
			onSubmit={e => {
				e.preventDefault();
				handleBorrow();
			}}
		>
<div className={styles.scrollContent}>
				<div className={styles.cards}>
					{/* Meter Number card — only when no pre-verified eligibility was supplied */}
					{needsMeter && (
						<section className={styles.card}>
							<header className={styles.cardHeader}>
								<span className={styles.cardLabel}>{t("common.labels.meterNumber")}</span>
							</header>
							<div className={styles.cardBody}>
								<input
									type="text"
									inputMode="numeric"
									placeholder={t("buyElectricity.web.enterPrepaidMeterNumber")}
									maxLength={18}
									value={meterNumber}
									onChange={e => setMeterNumber(e.target.value)}
									className={styles.meterInputField}
								/>
								{isValidatingMeter && (
									<div className={styles.meterStatus}>
										<span className={styles.meterStatusSpinner} />
										{t("buyElectricity.web.verifyingMeter")}
									</div>
								)}
								{!isValidatingMeter && verifiedAccountName && !meterError && (
									<div className={`${styles.meterStatus} ${styles.meterStatusOk}`}>
										<span className={styles.meterStatusIcon}>✓</span>
										<span className={styles.meterStatusName}>{verifiedAccountName}</span>
									</div>
								)}
								{!isValidatingMeter && meterError && (
									<div className={`${styles.meterStatus} ${styles.meterStatusError}`}>{meterError}</div>
								)}
							</div>
						</section>
					)}

					{/* Amount card — always rendered; disabled visual until meter is verified */}
					<section className={`${styles.card} ${!meterReady ? styles.cardDisabled : ""}`}>
						<header className={styles.cardHeader}>
							<span className={styles.cardLabel}>{t("common.labels.amount")}</span>
						</header>
						<div className={styles.cardBody}>
							<div className={`${styles.amountField} ${amountError ? styles.amountFieldError : ""}`}>
								<span className={styles.amountPrefix}>{s}</span>
								<NumericFormat
									value={amount}
									onValueChange={values => {
										setTouched(true);
										setAmount(values.value);
									}}
									thousandSeparator=","
									decimalSeparator="."
									decimalScale={2}
									allowNegative={false}
									placeholder="0.00"
									inputMode="decimal"
									className={styles.amountInput}
									disabled={!meterReady}
								/>
								<div className={styles.amountSuffix}>
									<span>{currency}</span>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
										<path
											d="M6 9l6 6 6-6"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</div>
							</div>
							{amountError ? (
								<p className={styles.errorText}>{amountError}</p>
							) : meterReady ? (
								<p className={styles.hintText}>
									{t("buyElectricity.web.minMaxHint", {
										min: `${s}${minimumBorrowAmount.toLocaleString()}`,
										max: `${s}${maximumBorrowAmount.toLocaleString()}`,
									})}
								</p>
							) : (
								<p className={styles.hintText}>{t("buyElectricity.web.verifyMeterToSetAmount")}</p>
							)}
						</div>
					</section>

					{/* Repayment card — always visible (informational); muted when meter not ready */}
					<section className={`${styles.card} ${!meterReady ? styles.cardDisabled : ""}`}>
						<header className={styles.cardHeader}>
							<span className={styles.cardLabel}>{t("buyElectricity.web.repayment")}</span>
						</header>
						<div className={styles.cardBody}>
							<p className={styles.repaymentText}>{t("buyElectricity.web.repaymentAutoDeduct")}</p>
						</div>
					</section>
				</div>
			</div>

			{/* Footer: Authorization card + Pay button */}
			<footer className={styles.footer}>
				<div className={styles.authCard}>
					<div className={styles.authMain}>
						<span className={styles.authTitle}>{t("common.labels.authorizationLabel")}</span>
						<p className={styles.authText}>
							{t("buyElectricity.web.youAuthorizeDeductPrefix", { utility: process.env.REACT_APP_UTILITY_NAME ?? "us" })}{" "}
							<strong>{s}{parsedAmount > 0 ? parsedAmount.toLocaleString() : "0.00"}</strong>{" "}
							{t("buyElectricity.web.fromNextPurchaseSuffix")}
						</p>
					</div>
					<SwitchButton
						isActive={authorized}
						onChange={setAuthorized}
						size="md"
						activeColor="var(--app-secondary)"
						disabled={!meterReady}
					/>
				</div>
				<Button type="submit" text={payLabel} disabled={!isValid} />
			</footer>
		</form>
	);
};
